// Sentry debe cargarse primero (ver docs Sentry React)
import "./instrument";
import { marcarHito } from "@/lib/analytics/hitosDelArranque";
import { createRoot } from "react-dom/client";
import { setSupabaseClient } from "@smarty/shared";
import { supabase } from "./lib/supabase";
import App from "./App.tsx";
import { devLog } from "@/lib/utils";
import { isAdminPath } from "@/config/routes.config";
import { initAppTheme } from "@/hooks/useAppTheme";
import { queryClient } from "@/lib/queryClient";
import { encenderCachePersistente } from "@/lib/cachePersistente";
import { iniciarTema } from "@/tema";

// El JavaScript termino de interpretarse: desde aca React puede montar. Primer hito del arranque.
marcarHito("js");

/*
 * ⭐ LA CACHE DE DISCO SE RESTAURA ANTES DE MONTAR. Si React montara primero, las pantallas leerian
 *    una cache vacia, pedirian todo, y lo de disco llegaria tarde: se pagaria el guardado sin
 *    cobrar el beneficio. Si IndexedDB no esta o falla, resuelve igual y el panel arranca como
 *    siempre — una memoria que no se puede leer no puede impedir entrar.
 *
 * 🪤 PERO CON TECHO. Esperar la cache de disco antes de montar tiene sentido —montar primero haria que
 *    las pantallas lean una cache vacia y pidan todo igual— pero esperarla SIN LIMITE convierte un
 *    IndexedDB lento en un arranque lento, que es exactamente lo que se vino a arreglar.
 *
 *    150 ms: si en ese tiempo no esta lista, se monta igual y la cache entra cuando llegue. Lo peor
 *    que pasa es pagar una consulta que se podria haber ahorrado; lo peor de no tener techo es que
 *    el usuario mire una pantalla en blanco por culpa del cache.
 */
const cacheLista = Promise.race([
  encenderCachePersistente(queryClient),
  new Promise<void>((listo) => setTimeout(listo, 150)),
]);

// Aplicar tema guardado ASAP (evita flash claro→oscuro en la primera pintura)
initAppTheme();
// La piel de Smarty (src/tema/): aplica la elegida en «Apariencia» (por defecto Nítido) antes del primer render.
iniciarTema();

// Inyectar cliente Supabase para uso en shared (monorepo)
setSupabaseClient(supabase);

/** Admin = base-minimal + polaris. Tienda = index.css + polaris. Sin mezclar (ver docs/CONVENCION-CSS.md). */
async function loadStyles(): Promise<void> {
  const pathname = window.location.pathname;
  if (isAdminPath(pathname)) {
    await import("./styles/base-minimal.css");
    await import("./styles/polaris.css");
  } else {
    await import("./index.css");
    await import("./styles/polaris.css");
  }
}

/*
 * 🪤 LOS ESTILOS Y LA CACHE VAN EN PARALELO, no uno después del otro. Son dos esperas que no
 *    dependen entre sí; encadenarlas sumaría sus tiempos por nada.
 */
Promise.all([loadStyles(), cacheLista]).then(() => {
  createRoot(document.getElementById("root")!).render(<App />);
});

/*
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * EL SERVICE WORKER, Y CUÁNDO NO
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * 🚨 `SIN_PWA=1` TIENE QUE APAGAR EL REGISTRO, NO SÓLO LA GENERACIÓN. Antes esta condición miraba
 * únicamente `import.meta.env.PROD`, que es true en un `vite preview` — así que toda revisión
 * local registraba (o intentaba registrar) un SW. Cuando lo conseguía, ese SW seguía sirviendo el
 * index.html cacheado en ese puerto para siempre: el servidor entregaba el build nuevo y el
 * navegador mostraba el viejo.
 *
 * 🪤 Y SE PRESENTA COMO «ESTOY VIENDO UNA VERSIÓN VIEJA», que es la peor forma de fallar en una
 * revisión visual: las dos partes tienen razón y nadie sospecha del service worker.
 *
 * Por eso, cuando la PWA está apagada, esto no sólo NO registra: DESREGISTRA lo que haya y borra
 * su caché. Un navegador que ya quedó envenenado se limpia solo en la siguiente carga.
 */
declare const __PWA_APAGADA__: boolean;
const pwaApagada = typeof __PWA_APAGADA__ !== 'undefined' && __PWA_APAGADA__;

if ("serviceWorker" in navigator && import.meta.env.PROD && !pwaApagada) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then((registration) => {
        devLog.warn("✅ Service Worker registrado:", registration.scope);
      })
      .catch((error) => {
        devLog.error("❌ Error al registrar Service Worker:", error);
      });
  });
} else if ("serviceWorker" in navigator) {
  // Sin PWA (dev o revisión local): se limpia lo que haya quedado registrado en este origen.
  void navigator.serviceWorker.getRegistrations().then((registros) => {
    registros.forEach((r) => void r.unregister());
  });
  /*
   * 🪤 DESREGISTRAR NO BORRA LO YA CACHEADO. Sin esto, el SW se va pero sus respuestas viejas
   * siguen en el Cache Storage, y la próxima carga puede seguir sirviendo el bundle anterior.
   */
  if ("caches" in globalThis) {
    void caches.keys().then((claves) => {
      claves.filter((k) => k.startsWith('workbox') || k.includes('precache') || k.includes('smarty'))
        .forEach((k) => void caches.delete(k));
    });
  }
}
